import type { WorkflowTask } from '../../components/ProjectWorkflow';
import { normalizeInvoiceRole, type InvoiceRole } from '../../modules/docs/utils/invoiceLedger';
import {
  invoiceDueDate,
  normalizePaymentTermsInDays,
  toDateOnly,
} from '../../modules/docs/utils/invoicePaymentTerms';
import { workspaceLifecycleStatus } from '../../utils/workflowUtils';
import {
  OPERATIONAL_QUEUES,
  OPERATIONAL_STATES,
  deriveOperationalFacts,
  deriveOperationalState,
  isIssuedUnpaidInvoice,
  isLiveIssuedInvoice,
  type OperationalFacts,
  type OperationalQueueId,
  type OperationalState,
  type OperationalStateId,
} from './operationalState';
import {
  installDisplayDay,
  installSpanNote,
  jobCardInstallSpan,
  londonDayKey,
  toInstallDateOnly,
  toInstallTimeOnly,
  type InstallSpan,
} from './jobCardInstallDate';
import {
  carryingCards,
  groupCompletionsByCard,
  jobCardCompletion,
  type JobCardCompletion,
  type JobCompletionRow,
} from './openJobCards.model';
import { ACTION_TAB_LABELS, stageFor, type WorkspaceActionTab, type WorkspaceStage } from './workspaceStages';
import { deriveRecordTicks, type RecordTicks } from './checklistRecords';

export { isIssuedUnpaidInvoice, isLiveIssuedInvoice };

export type DashboardView = 'today' | 'board' | 'live' | 'presale' | 'week' | 'scheduled' | 'unscheduled';
export type AttentionSide = 'office' | 'customer';
export type AttentionKind =
  | 'install_order_missing'
  | 'install_deposit_missing'
  | 'install_unassigned'
  | 'works_overdue'
  | 'variation_unpaid'
  | 'deposit_unpaid'
  | 'accepted_no_deposit'
  | 'accepted_no_balance'
  | 'accepted_not_ordered'
  | 'deposit_paid_not_ordered'
  | 'ordered_no_production'
  | 'works_awaiting_payment'
  | 'production_no_install'
  | 'quote_silent'
  | 'actuals_unreviewed';

export const DASHBOARD_VIEWS: Array<{ id: DashboardView; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'board', label: 'Board' },
  { id: 'presale', label: 'Pre-Sale' },
  { id: 'live', label: 'Live Job' },
  { id: 'week', label: 'This week' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'unscheduled', label: 'Not Scheduled' },
];

export const WORKSPACE_OPS_VIEW_KEY = 'workspaces_ops_view';

export function isDashboardView(value: string | null | undefined): value is DashboardView {
  return DASHBOARD_VIEWS.some((view) => view.id === value);
}

export type OpsProject = {
  id: string;
  project_name?: string | null;
  customer_name?: string | null;
  supply_only?: boolean | null;
  complete?: boolean | null;
  project_status?: 'active' | 'completed' | 'cancelled' | null;
  status?: string | null;
  workflow_tasks?: WorkflowTask[] | null;
};

export type OpsDocument = {
  id: string;
  project_id?: string | null;
  kind?: string | null;
  status?: string | null;
  gross_total?: number | null;
  issued_at?: string | null;
  paid_at?: string | null;
  sent_at?: string | null;
  accepted_at?: string | null;
  created_at?: string | null;
  invoice_role?: string | null;
  payment_terms_in_days?: number | null;
  deposit_amount?: number | null;
  has_deposit?: boolean | null;
  invoice_ref?: string | null;
};

export type OpsJobCard = {
  id: string;
  project_id: string;
  title?: string | null;
  install_date_override?: string | null;
  install_end_date_override?: string | null;
  follows_booking?: boolean | null;
};

export type OpsAssignee = {
  job_card_id: string;
  project_id: string;
  full_name?: string | null;
};

export type OpsProjectEvent = {
  project_id: string;
  event_type?:
    | 'installationDateConfirmation'
    | 'deliveryDateConfirmation'
    | 'depositRequest'
    | 'firstSurveyAppointmentConfirmation'
    | 'finalSurveyAppointmentConfirmation'
    | 'requestReview'
    | null;
  event_date?: string | null;
  end_date?: string | null;
  metadata?: unknown;
  status?: string | null;
  email_status?: string | null;
  email_sent_at?: string | null;
  created_at?: string | null;
};

export type OpsShareLink = {
  document_id: string;
  created_at?: string | null;
  last_sent_at?: string | null;
};

export type AttentionRow = {
  projectId: string;
  workspace: string;
  customer: string;
  why: string;
  ageLabel: string;
  ageDays: number;
  next: string;
  href: string;
  side: AttentionSide;
  kind: AttentionKind;
  queue: OperationalQueueId;
  state: OperationalStateId;
  /** Operational exception: sorts first and always counts as Needs office. */
  exception: boolean;
  tone: 'danger' | 'warning' | 'primary';
  amount: number;
};

export type BoardCard = {
  projectId: string;
  workspace: string;
  customer: string;
  queue: OperationalQueueId;
  state: OperationalStateId;
  stateLabel: string;
  exception: boolean;
  flags: string[];
  href: string;
  note?: string;
  flag?: AttentionSide | null;
  /** Quote accepted (or a deposit invoice raised): a live job rather than pre-sale. */
  accepted: boolean;
  /** The most advanced date on file: install (or delivery) > production > final survey > first survey. */
  keyDate: KeyDate | null;
};

export type KeyDate = { day: string; label: 'Install' | 'Delivery' | 'Production' | 'Final survey' | 'First survey' };

/** Active workspaces split by whether the customer has accepted: Live Job or Pre-Sale. */
export function boardCardsByAcceptance(ops: WorkspaceOperations, accepted: boolean): BoardCard[] {
  return ops.board
    .filter((card) => card.accepted === accepted)
    .sort((a, b) => a.workspace.localeCompare(b.workspace) || a.customer.localeCompare(b.customer));
}

export type WeekInstallRow = {
  projectId: string;
  jobCardId: string | null;
  dayKey: string;
  time: string;
  /** e.g. "Day 2 of 5 · until Fri 2 Oct" for multi-day installs. */
  spanNote: string | null;
  workspace: string;
  customer: string;
  crew: string;
  source: 'Calendar' | 'Override';
  /** Null when the card is not marked complete, or when there is no job card. */
  completion: JobCardCompletion | null;
  href: string;
};

export type MoneyBucket = {
  label: string;
  amount: number;
  count: number;
};

/** A job whose installation (or delivery) date has passed but which is not marked complete. */
export type PendingClosureRow = {
  projectId: string;
  workspace: string;
  customer: string;
  /** Last booked day on site (or the delivery day). */
  fittedOn: string;
  daysSince: number;
  kind: 'install' | 'delivery';
  /** What still stands between this job and closing it; empty means ready to close. */
  holdUps: string[];
  href: string;
};

/** Days ahead (today included) a booking is checked for missing order, deposit or crew. */
export const INSTALL_RISK_WINDOW_DAYS = 14;

/** A booking inside the risk window with something still missing for the day on site. */
export type InstallRiskRow = {
  projectId: string;
  workspace: string;
  customer: string;
  /** Next booked day on site, today or later. */
  nextDay: string;
  daysUntil: number;
  kind: 'install' | 'delivery';
  /** Never empty: a booking with nothing missing is not at risk. */
  holdUps: string[];
  href: string;
};

/** Ordered but not booked yet; empty hold-ups means a date can be offered now. */
export type ReadyToBookRow = {
  projectId: string;
  workspace: string;
  customer: string;
  productionDate: string | null;
  /** When the order went in, for rows still waiting on a production date. */
  orderedOn: string | null;
  daysWaiting: number;
  kind: 'install' | 'delivery';
  holdUps: string[];
  href: string;
};

export type ScheduledJobRow = {
  projectId: string;
  jobCardId: string | null;
  dayKey: string;
  time: string;
  spanNote: string | null;
  workspace: string;
  customer: string;
  crew: string;
  source: 'Calendar' | 'Override';
  completion: JobCardCompletion | null;
  href: string;
  kind: 'install' | 'delivery';
};

export type UnscheduledJobRow = {
  projectId: string;
  workspace: string;
  customer: string;
  productionDate: string;
  href: string;
  kind: 'install' | 'delivery';
};

/** Where one workspace is and what happens next: the same answer the Board and Today give. */
export type WorkspaceSnapshot = {
  projectId: string;
  stage: WorkspaceStage;
  /** Null for completed / cancelled workspaces. */
  state: OperationalStateId | null;
  stateLabel: string;
  exception: boolean;
  flags: string[];
  /** Why it needs attention, if it does. */
  reason: string | null;
  side: AttentionSide | null;
  /** Tab to open for the next step, and its link. */
  next: string | null;
  href: string;
  /** Checklist steps ticked by a record (paid invoice, booking), with where each comes from. */
  recordTicks: RecordTicks;
};

export type WorkspaceOperations = {
  activeCount: number;
  queueCounts: Record<OperationalQueueId, number>;
  stateCounts: Record<OperationalStateId, number>;
  exceptionCount: number;
  attention: AttentionRow[];
  board: BoardCard[];
  week: WeekInstallRow[];
  scheduled: ScheduledJobRow[];
  unscheduled: UnscheduledJobRow[];
  money: MoneyBucket[];
  officeReasons: Array<{ label: string; count: number }>;
  customerReasons: Array<{ label: string; count: number }>;
  needsOffice: number;
  waitingCustomer: number;
  installsThisWeek: number;
  issuedUnpaidTotal: number;
  /** Fitted (or delivered) before today and not yet closed, oldest first. */
  pendingClosure: PendingClosureRow[];
  /** Booked within the next INSTALL_RISK_WINDOW_DAYS with order, deposit or crew missing, soonest first. */
  installsAtRisk: InstallRiskRow[];
  /** Ordered, not booked: production-dated first (soonest), then oldest order. */
  readyToBook: ReadyToBookRow[];
  /** One per active workspace, keyed by project id. */
  snapshots: Record<string, WorkspaceSnapshot>;
};

function statusOf(value?: string | null) {
  return String(value || '').trim().toLowerCase();
}

function roleOf(doc: OpsDocument): InvoiceRole | null {
  return normalizeInvoiceRole(doc.invoice_role);
}

function amountOf(doc: OpsDocument): number {
  const value = Number(doc.gross_total || 0);
  return Number.isFinite(value) ? value : 0;
}

export function invoiceIssueDate(doc: OpsDocument): string | null {
  return toDateOnly(doc.issued_at) || toDateOnly(doc.created_at);
}

export function isInvoiceOverdue(doc: OpsDocument, today: string): boolean {
  if (!isIssuedUnpaidInvoice(doc)) return false;
  const issued = invoiceIssueDate(doc);
  if (!issued) return false;
  const terms = normalizePaymentTermsInDays(doc.payment_terms_in_days, doc.invoice_role);
  return invoiceDueDate(issued, terms) < today;
}

function calendarDaysBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const start = Date.UTC(fy, fm - 1, fd);
  const end = Date.UTC(ty, tm - 1, td);
  return Math.round((end - start) / 86_400_000);
}

export function ageLabel(days: number): string {
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export function londonDayKeysAhead(startDay: string, count: number): string[] {
  const [year, month, day] = startDay.split('-').map(Number);
  const keys: string[] = [];
  for (let index = 0; index < count; index += 1) {
    const next = new Date(Date.UTC(year, month - 1, day + index, 12));
    keys.push(londonDayKey(next.toISOString()));
  }
  return keys;
}

/** Current and upcoming first (soonest first), then past dates newest to oldest. */
export function compareCurrentToOldest(aDay: string, bDay: string, today: string): number {
  const aCurrent = aDay >= today;
  const bCurrent = bDay >= today;
  if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
  return aCurrent ? aDay.localeCompare(bDay) : bDay.localeCompare(aDay);
}

function workspaceHref(projectId: string, stage: WorkspaceActionTab, docId?: string | null) {
  const base = `/projects/${encodeURIComponent(projectId)}/workspace/${stage}`;
  return docId ? `${base}?doc=${encodeURIComponent(docId)}` : base;
}

function queueHref(projectId: string, queue: OperationalQueueId) {
  if (queue === 'presale') return workspaceHref(projectId, 'quotes');
  if (queue === 'deposit') return workspaceHref(projectId, 'invoices');
  if (queue === 'aftercare') return workspaceHref(projectId, 'aftercare');
  if (queue === 'ordering') return workspaceHref(projectId, 'supplier');
  return workspaceHref(projectId, 'implementation');
}

function crewLabel(names: string[]): string {
  const unique = [...new Set(names.map((name) => name.trim()).filter(Boolean))];
  return unique.length ? unique.join(', ') : 'Unassigned';
}

function formatWeekDay(dayKey: string): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/London',
  });
}

export function formatWeekDayLabel(dayKey: string): string {
  return formatWeekDay(dayKey);
}

type ProjectContext = {
  project: OpsProject;
  facts: OperationalFacts;
  state: OperationalState;
  docs: OpsDocument[];
  cards: OpsJobCard[];
  assignees: OpsAssignee[];
  calendarStamp: string | null;
  /** One entry per job card, or the calendar invite alone when there are no cards. */
  installSpans: CardSpan[];
  deliveryStamp: string | null;
  actualsDue: boolean;
};

type CardSpan = { cardId: string | null; span: InstallSpan };

function classifyAttention(ctx: ProjectContext, today: string, weekKeys: Set<string>): AttentionRow | null {
  const { project, facts, state, docs, assignees, calendarStamp, installSpans, deliveryStamp, actualsDue } = ctx;
  const invoices = docs.filter((doc) => doc.kind === 'Invoice');
  const assignedCardIds = new Set(assignees.map((row) => row.job_card_id));
  const weekDays = new Set<string>();
  const crewedDays = new Set<string>();
  installSpans.forEach(({ cardId, span }) => {
    const crewed = cardId ? assignedCardIds.has(cardId) : assignees.length > 0;
    span.days.forEach((day) => {
      if (!weekKeys.has(day)) return;
      weekDays.add(day);
      if (crewed) crewedDays.add(day);
    });
  });
  const uncrewedDay = Array.from(weekDays).sort().find((day) => !crewedDays.has(day));

  const overdueWorks = invoices.find((doc) => roleOf(doc) === 'works' && isInvoiceOverdue(doc, today));
  const unpaidVariation = invoices.find((doc) => roleOf(doc) === 'variation' && isIssuedUnpaidInvoice(doc));
  const unpaidWorks = invoices.find((doc) => roleOf(doc) === 'works' && isIssuedUnpaidInvoice(doc));
  const quote = facts.acceptedQuote;
  const silent = !facts.accepted && statusOf(facts.latestQuote?.status) === 'sent';

  const workspace = project.project_name || 'Untitled';
  const customer = project.customer_name || '—';
  const eventDay = (iso?: string | null) => toDateOnly(iso) || today;
  const since = (iso?: string | null) => Math.max(0, calendarDaysBetween(eventDay(iso), today));
  const row = (
    partial: Omit<AttentionRow, 'projectId' | 'workspace' | 'customer' | 'queue' | 'state' | 'exception' | 'ageLabel'>
  ): AttentionRow => ({
    projectId: project.id,
    workspace,
    customer,
    queue: state.queue,
    state: state.id,
    exception: false,
    ageLabel: ageLabel(partial.ageDays),
    ...partial,
  });
  const depositHref = workspaceHref(project.id, 'invoices', facts.depositInvoice?.id);
  // Ordering work (order placed, production dates) happens on the Ordering tab
  const orderingHref = workspaceHref(project.id, 'supplier');
  const orderingNext = ACTION_TAB_LABELS.supplier;

  if (state.id === 'booked_order_not_placed') {
    return {
      ...row({
        why: state.label,
        ageDays: since(facts.depositPaidAt || facts.acceptedAt),
        next: orderingNext,
        href: orderingHref,
        side: 'office',
        kind: 'install_order_missing',
        tone: 'danger',
        amount: 0,
      }),
      exception: true,
    };
  }
  if (state.id === 'booked_deposit_not_received') {
    return {
      ...row({
        why: state.label,
        ageDays: since(facts.depositRequestedAt || facts.acceptedAt),
        next: 'Invoices',
        href: depositHref,
        side: 'office',
        kind: 'install_deposit_missing',
        tone: 'danger',
        amount: facts.depositAmount,
      }),
      exception: true,
    };
  }

  if (uncrewedDay) {
    return row({
      why: crewedDays.size ? 'Install this week, crew missing some days' : 'Install this week, no crew',
      ageDays: Math.max(0, calendarDaysBetween(today, uncrewedDay)),
      next: 'Installation',
      href: workspaceHref(project.id, 'implementation'),
      side: 'office',
      kind: 'install_unassigned',
      tone: 'danger',
      amount: 0,
    });
  }
  if (overdueWorks) {
    return row({
      why: 'Works invoice overdue',
      ageDays: calendarDaysBetween(invoiceDueDate(
        invoiceIssueDate(overdueWorks) || today,
        normalizePaymentTermsInDays(overdueWorks.payment_terms_in_days, overdueWorks.invoice_role)
      ), today),
      next: 'Invoices',
      href: workspaceHref(project.id, 'invoices', overdueWorks.id),
      side: 'customer',
      kind: 'works_overdue',
      tone: 'danger',
      amount: amountOf(overdueWorks),
    });
  }
  if (unpaidVariation) {
    return row({
      why: 'Variation issued, not paid',
      ageDays: calendarDaysBetween(eventDay(unpaidVariation.issued_at || unpaidVariation.created_at), today),
      next: 'Invoices',
      href: workspaceHref(project.id, 'invoices', unpaidVariation.id),
      side: 'customer',
      kind: 'variation_unpaid',
      tone: 'warning',
      amount: amountOf(unpaidVariation),
    });
  }
  const supplyOnly = facts.supplyOnly;
  const balanceNotRaised = () => row({
    why: 'Accepted, no deposit, balance not raised',
    ageDays: since(facts.acceptedAt),
    next: 'Invoices',
    href: workspaceHref(project.id, 'invoices'),
    side: 'office',
    kind: 'accepted_no_balance',
    tone: 'warning',
    amount: quote ? amountOf(quote) : 0,
  });

  switch (state.id) {
    case 'awaiting_deposit_request':
      return row({
        why: facts.depositInvoice ? 'Accepted, deposit request not sent' : 'Accepted, deposit not raised',
        ageDays: since(facts.acceptedAt),
        next: 'Invoices',
        href: depositHref,
        side: 'office',
        kind: 'accepted_no_deposit',
        tone: 'warning',
        amount: facts.depositAmount,
      });
    case 'deposit_requested':
      return row({
        why: 'Deposit requested, not paid',
        ageDays: since(facts.depositRequestedAt),
        next: 'Invoices',
        href: depositHref,
        side: 'customer',
        kind: 'deposit_unpaid',
        tone: 'danger',
        amount: facts.depositAmount,
      });
    case 'deposit_received_not_ordered':
      return row({
        why: 'Deposit received, not ordered',
        ageDays: since(facts.depositPaidAt),
        next: orderingNext,
        href: orderingHref,
        side: 'office',
        kind: 'deposit_paid_not_ordered',
        tone: 'warning',
        amount: 0,
      });
    case 'accepted_not_ordered':
      if (!facts.worksIssued) return balanceNotRaised();
      if (unpaidWorks) break;
      return row({
        why: 'Accepted, not ordered',
        ageDays: since(facts.acceptedAt),
        next: orderingNext,
        href: orderingHref,
        side: 'office',
        kind: 'accepted_not_ordered',
        tone: 'warning',
        amount: 0,
      });
    case 'ordered_awaiting_production':
      return row({
        why: 'Ordered, no production date',
        ageDays: since(facts.orderedAt || facts.depositPaidAt || facts.acceptedAt),
        next: orderingNext,
        href: orderingHref,
        side: 'office',
        kind: 'ordered_no_production',
        tone: 'warning',
        amount: 0,
      });
    case 'production_confirmed_not_booked':
      return row({
        why: supplyOnly ? 'Production date set, no delivery date' : 'Production date set, no install date',
        ageDays: since(facts.productionDate),
        next: ACTION_TAB_LABELS.implementation,
        href: workspaceHref(project.id, 'implementation'),
        side: 'office',
        kind: 'production_no_install',
        tone: 'warning',
        amount: 0,
      });
    case 'installed': {
      // Close-out after fitting: the deposit step is historical, only billing gaps matter.
      const deposit = facts.depositInvoice;
      if (deposit && !facts.depositPaid && isLiveIssuedInvoice(deposit)) {
        return row({
          why: 'Deposit issued, not paid',
          ageDays: since(facts.depositRequestedAt || deposit.issued_at || deposit.created_at),
          next: 'Invoices',
          href: depositHref,
          side: 'customer',
          kind: 'deposit_unpaid',
          tone: 'danger',
          amount: amountOf(deposit),
        });
      }
      if (facts.depositRequired && !deposit) {
        return row({
          why: 'Accepted, deposit not raised',
          ageDays: since(facts.acceptedAt),
          next: 'Invoices',
          href: depositHref,
          side: 'office',
          kind: 'accepted_no_deposit',
          tone: 'warning',
          amount: facts.depositAmount,
        });
      }
      if (quote && !facts.depositRequired && !facts.worksIssued) return balanceNotRaised();
      break;
    }
    default:
      break;
  }
  if (unpaidWorks) {
    return row({
      why: 'Balance issued, awaiting payment',
      ageDays: calendarDaysBetween(eventDay(unpaidWorks.issued_at || unpaidWorks.created_at), today),
      next: 'Invoices',
      href: workspaceHref(project.id, 'invoices', unpaidWorks.id),
      side: 'customer',
      kind: 'works_awaiting_payment',
      tone: 'primary',
      amount: amountOf(unpaidWorks),
    });
  }
  const sentQuote = facts.latestQuote;
  if (silent && sentQuote) {
    return row({
      why: 'Quote sent, no response',
      ageDays: calendarDaysBetween(eventDay(sentQuote.sent_at || sentQuote.created_at), today),
      next: 'Quotes',
      href: workspaceHref(project.id, 'quotes', sentQuote.id),
      side: 'customer',
      kind: 'quote_silent',
      tone: 'warning',
      amount: amountOf(sentQuote),
    });
  }
  if (actualsDue) {
    const installedStamp = supplyOnly ? deliveryStamp : calendarStamp;
    return row({
      why: 'Install booked, Job Record not complete',
      ageDays: installedStamp ? Math.max(0, calendarDaysBetween(toInstallDateOnly(installedStamp) || today, today)) : 0,
      next: 'Aftercare',
      href: workspaceHref(project.id, 'aftercare'),
      side: 'office',
      kind: 'actuals_unreviewed',
      tone: 'warning',
      amount: 0,
    });
  }
  return null;
}

function latestCalendarEvent(events: OpsProjectEvent[]): OpsProjectEvent | null {
  const live = events.filter((event) => statusOf(event.status) !== 'cancelled');
  if (!live.length) return null;
  return live.reduce((latest, event) => {
    const latestTs = Date.parse(latest.event_date || latest.created_at || '') || 0;
    const eventTs = Date.parse(event.event_date || event.created_at || '') || 0;
    return eventTs >= latestTs ? event : latest;
  });
}

export function assembleWorkspaceOperations(input: {
  projects: OpsProject[];
  documents: OpsDocument[];
  cards: OpsJobCard[];
  assignees: OpsAssignee[];
  events: OpsProjectEvent[];
  shareLinks?: OpsShareLink[];
  completions?: JobCompletionRow[];
  /** Final Surveys marked complete, one row per workspace. */
  finalSurveys?: Array<{ project_id: string; completed_at: string | null }>;
  /** Workspaces in the Job Records To Be Reviewed queue. */
  actualsDueProjectIds?: Set<string>;
  now?: Date;
}): WorkspaceOperations {
  const now = input.now || new Date();
  const today = londonDayKey(now.toISOString());
  const weekList = londonDayKeysAhead(today, 7);
  const weekKeys = new Set(weekList);
  const docsByProject = new Map<string, OpsDocument[]>();
  input.documents.forEach((doc) => {
    const projectId = String(doc.project_id || '');
    if (!projectId) return;
    const list = docsByProject.get(projectId) || [];
    list.push(doc);
    docsByProject.set(projectId, list);
  });
  const cardsByProject = new Map<string, OpsJobCard[]>();
  input.cards.forEach((card) => {
    const list = cardsByProject.get(card.project_id) || [];
    list.push(card);
    cardsByProject.set(card.project_id, list);
  });
  const assigneesByProject = new Map<string, OpsAssignee[]>();
  input.assignees.forEach((row) => {
    const list = assigneesByProject.get(row.project_id) || [];
    list.push(row);
    assigneesByProject.set(row.project_id, list);
  });
  const eventsByProject = new Map<string, OpsProjectEvent[]>();
  input.events.forEach((event) => {
    const list = eventsByProject.get(event.project_id) || [];
    list.push(event);
    eventsByProject.set(event.project_id, list);
  });
  const shareLinks = input.shareLinks || [];
  const completionsByCard = groupCompletionsByCard(input.completions || []);
  const finalSurveyDoneAt = new Map((input.finalSurveys || []).map((row) => [row.project_id, row.completed_at || '']));

  const queueCounts = Object.fromEntries(
    OPERATIONAL_QUEUES.map((queue) => [queue.id, 0]),
  ) as Record<OperationalQueueId, number>;
  const stateCounts = Object.fromEntries(
    (Object.keys(OPERATIONAL_STATES) as OperationalStateId[]).map((id) => [id, 0]),
  ) as Record<OperationalStateId, number>;
  const attention: AttentionRow[] = [];
  const board: BoardCard[] = [];
  const week: WeekInstallRow[] = [];
  const scheduled: ScheduledJobRow[] = [];
  const unscheduled: UnscheduledJobRow[] = [];
  const pendingClosure: PendingClosureRow[] = [];
  const installsAtRisk: InstallRiskRow[] = [];
  const readyToBook: ReadyToBookRow[] = [];
  const riskKeys = new Set(londonDayKeysAhead(today, INSTALL_RISK_WINDOW_DAYS));
  const money = {
    deposit: { label: 'Deposits issued, unpaid', amount: 0, count: 0 },
    works: { label: 'Works / balance overdue', amount: 0, count: 0 },
    variation: { label: 'Variations unpaid', amount: 0, count: 0 },
    accepted: { label: 'Accepted, no deposit yet', amount: 0, count: 0 },
  };
  let issuedUnpaidTotal = 0;
  const officeCount: Record<string, number> = {};
  const snapshots: Record<string, WorkspaceSnapshot> = {};
  const customerCount: Record<string, number> = {};

  const active = input.projects.filter((project) => workspaceLifecycleStatus(project) === 'Active');

  active.forEach((project) => {
    const docs = docsByProject.get(project.id) || [];
    const cards = cardsByProject.get(project.id) || [];
    const assignees = assigneesByProject.get(project.id) || [];
    const projectEvents = eventsByProject.get(project.id) || [];
    const recordedFacts = deriveOperationalFacts({ project, documents: docs, events: projectEvents, shareLinks });
    const calendarEvent = latestCalendarEvent(
      projectEvents.filter((event) => (event.event_type || 'installationDateConfirmation') === 'installationDateConfirmation'),
    );
    const calendarStamp = calendarEvent?.event_date || null;
    const deliveryEvent = latestCalendarEvent(
      projectEvents.filter((event) => event.event_type === 'deliveryDateConfirmation'),
    );
    const deliveryStamp = deliveryEvent?.event_date || null;
    // The job's own booking: the delivery for supply-only jobs, the installation otherwise
    const bookingEvent = recordedFacts.supplyOnly ? deliveryEvent : calendarEvent;
    // An ended card hands the job on to a later card, so its dates no longer count: only the
    // cards still carrying the job say whether, and when, it is booked.
    const installSpans: CardSpan[] = cards.length
      ? carryingCards(cards, completionsByCard)
        .map((card) => ({
          cardId: card.id,
          span: jobCardInstallSpan({
            calendar: bookingEvent,
            overrideStart: card.install_date_override,
            overrideEnd: card.install_end_date_override,
            followsBooking: card.follows_booking !== false,
          }),
        }))
      : [{ cardId: null, span: jobCardInstallSpan({ calendar: bookingEvent }) }];
    const carryingDays = Array.from(new Set(installSpans.flatMap(({ span }) => span.days))).sort();
    // The job is done once every card still carrying it is marked complete, by the office or a fitter
    const cardFinishes = carryingCards(cards, completionsByCard)
      .map((card) => jobCardCompletion(completionsByCard.get(card.id) || []));
    const jobCardsCompletedAt = cardFinishes.length && cardFinishes.every((done) => done?.kind === 'completed')
      ? cardFinishes.map((done) => done!.at).sort().slice(-1)[0]
      : null;
    const facts: OperationalFacts = cards.length
      ? {
        ...recordedFacts,
        bookingDate: carryingDays[0] || null,
        installDone: recordedFacts.installDone || !!jobCardsCompletedAt,
      }
      : recordedFacts;
    const state = deriveOperationalState(facts);
    queueCounts[state.queue] += 1;
    stateCounts[state.id] += 1;
    // The Job Records reminder only applies once the carrying cards' days are past.
    const actualsDue = !!input.actualsDueProjectIds?.has(project.id)
      && (!cards.length || (carryingDays.length > 0 && carryingDays[carryingDays.length - 1] < today));
    const hit = classifyAttention({
      project,
      facts,
      state,
      docs,
      cards,
      assignees,
      calendarStamp,
      installSpans,
      deliveryStamp,
      actualsDue,
    }, today, weekKeys);

    docs.forEach((doc) => {
      if (!isIssuedUnpaidInvoice(doc)) return;
      issuedUnpaidTotal += amountOf(doc);
      const role = roleOf(doc);
      if (role === 'deposit') {
        money.deposit.amount += amountOf(doc);
        money.deposit.count += 1;
      }
      if (role === 'works' && isInvoiceOverdue(doc, today)) {
        money.works.amount += amountOf(doc);
        money.works.count += 1;
      }
      if (role === 'variation') {
        money.variation.amount += amountOf(doc);
        money.variation.count += 1;
      }
    });
    if (hit?.kind === 'accepted_no_deposit') {
      money.accepted.amount += hit.amount;
      money.accepted.count += 1;
    }

    const surveyDay = (type: OpsProjectEvent['event_type']) => {
      const event = latestCalendarEvent(projectEvents.filter((row) => row.event_type === type));
      return toInstallDateOnly(event?.event_date) || null;
    };
    const bookingDay = carryingDays.length
      ? installDisplayDay(carryingDays, today)
      : (cards.length ? null : toInstallDateOnly(facts.bookingDate) || null);
    const finalSurveyDay = surveyDay('finalSurveyAppointmentConfirmation');
    const firstSurveyDay = surveyDay('firstSurveyAppointmentConfirmation');
    const keyDate: KeyDate | null = bookingDay ? { day: bookingDay, label: facts.supplyOnly ? 'Delivery' : 'Install' }
      : facts.productionDate ? { day: facts.productionDate, label: 'Production' }
        : finalSurveyDay ? { day: finalSurveyDay, label: 'Final survey' }
          : firstSurveyDay ? { day: firstSurveyDay, label: 'First survey' }
            : null;

    board.push({
      projectId: project.id,
      workspace: project.project_name || 'Untitled',
      customer: project.customer_name || '—',
      queue: state.queue,
      state: state.id,
      stateLabel: state.label,
      exception: state.exception,
      flags: state.flags,
      href: hit?.href || queueHref(project.id, state.queue),
      note: hit?.why,
      flag: hit?.side || null,
      accepted: facts.accepted,
      keyDate,
    });
    snapshots[project.id] = {
      projectId: project.id,
      stage: stageFor('Active', state.id),
      state: state.id,
      stateLabel: state.label,
      exception: state.exception,
      flags: state.flags,
      reason: hit?.why || null,
      side: hit?.side || null,
      next: hit?.next || null,
      href: hit?.href || queueHref(project.id, state.queue),
      recordTicks: deriveRecordTicks(facts, docs, {
        finalSurveyCompletedAt: finalSurveyDoneAt.get(project.id) ?? null,
        jobCardsCompletedAt,
        reviewRequestedAt: projectEvents
          .filter((event) => event.event_type === 'requestReview' && String(event.email_status || '').toLowerCase() === 'sent')
          .map((event) => event.email_sent_at || event.created_at || '')
          .sort()[0] || null,
      }),
    };
    if (hit) {
      attention.push(hit);
      const bucket = hit.side === 'office' ? officeCount : customerCount;
      bucket[hit.why] = (bucket[hit.why] || 0) + 1;
    }

    const namesByCard = new Map<string, string[]>();
    assignees.forEach((row) => {
      const list = namesByCard.get(row.job_card_id) || [];
      if (row.full_name) list.push(row.full_name);
      namesByCard.set(row.job_card_id, list);
    });
    const allNames = assignees.map((row) => row.full_name || '').filter(Boolean);
    const scheduleSpans = installSpans;
    const datedSpans = scheduleSpans.filter(({ span }) => span.days.length > 0);
    if (datedSpans.length) {
      const allDays = Array.from(new Set(datedSpans.flatMap(({ span }) => span.days))).sort();
      const fittedOn = allDays[allDays.length - 1];
      if (fittedOn < today) {
        const invoices = docs.filter((doc) => doc.kind === 'Invoice');
        const holdUps: string[] = [];
        if (actualsDue) holdUps.push('Job Record not done');
        if (invoices.some((doc) => roleOf(doc) === 'deposit' && isIssuedUnpaidInvoice(doc))) holdUps.push('Deposit unpaid');
        if (invoices.some((doc) => roleOf(doc) === 'works' && isIssuedUnpaidInvoice(doc))) holdUps.push('Balance unpaid');
        else if (facts.accepted && !facts.worksIssued) holdUps.push('Balance not raised');
        if (invoices.some((doc) => roleOf(doc) === 'variation' && isIssuedUnpaidInvoice(doc))) holdUps.push('Variation unpaid');
        pendingClosure.push({
          projectId: project.id,
          workspace: project.project_name || 'Untitled',
          customer: project.customer_name || '—',
          fittedOn,
          daysSince: calendarDaysBetween(fittedOn, today),
          kind: facts.supplyOnly ? 'delivery' : 'install',
          holdUps,
          href: workspaceHref(project.id, 'aftercare'),
        });
      }
      const upcoming = allDays.filter((day) => riskKeys.has(day));
      if (upcoming.length && !facts.installDone) {
        const assignedCardIds = new Set(assignees.map((row) => row.job_card_id));
        const uncrewed = upcoming.filter((day) => !datedSpans.some(({ cardId, span }) => (
          span.days.includes(day) && (cardId ? assignedCardIds.has(cardId) : assignees.length > 0)
        )));
        const holdUps: string[] = [];
        if (facts.accepted && !facts.orderPlaced && !facts.productionDate) holdUps.push('Order not placed');
        if (facts.depositRequired && !facts.depositPaid) holdUps.push('Deposit not received');
        if (uncrewed.length === upcoming.length) holdUps.push('No crew');
        else if (uncrewed.length) holdUps.push('Crew missing some days');
        if (holdUps.length) {
          installsAtRisk.push({
            projectId: project.id,
            workspace: project.project_name || 'Untitled',
            customer: project.customer_name || '—',
            nextDay: upcoming[0],
            daysUntil: calendarDaysBetween(today, upcoming[0]),
            kind: facts.supplyOnly ? 'delivery' : 'install',
            holdUps,
            href: workspaceHref(project.id, holdUps[0] === 'Order not placed' ? 'supplier' : holdUps[0] === 'Deposit not received' ? 'invoices' : 'implementation'),
          });
        }
      }
      const dayKey = installDisplayDay(allDays, today);
      const lead = datedSpans.find(({ span }) => span.days.includes(dayKey)) || datedSpans[0];
      scheduled.push({
        projectId: project.id,
        jobCardId: datedSpans.length === 1 ? datedSpans[0].cardId : null,
        dayKey,
        time: toInstallTimeOnly(lead.span.start) || '—',
        spanNote: installSpanNote(allDays, dayKey),
        workspace: project.project_name || 'Untitled',
        customer: project.customer_name || '—',
        crew: crewLabel(allNames),
        source: datedSpans.some(({ span }) => span.overridden) ? 'Override' : 'Calendar',
        completion: datedSpans.length === 1 && datedSpans[0].cardId
          ? jobCardCompletion(completionsByCard.get(datedSpans[0].cardId) || [])
          : null,
        href: workspaceHref(project.id, 'implementation'),
        kind: facts.supplyOnly ? 'delivery' : 'install',
      });
    } else if (facts.productionDate) {
      unscheduled.push({
        projectId: project.id,
        workspace: project.project_name || 'Untitled',
        customer: project.customer_name || '—',
        productionDate: facts.productionDate,
        href: workspaceHref(project.id, 'implementation'),
        kind: facts.supplyOnly ? 'delivery' : 'install',
      });
    }
    if (state.id === 'production_confirmed_not_booked' || state.id === 'ordered_awaiting_production') {
      const holdUps: string[] = [];
      if (!facts.productionDate) holdUps.push('No production date');
      if (facts.depositRequired && !facts.depositPaid) holdUps.push('Deposit unpaid');
      const orderedOn = toDateOnly(facts.orderedAt);
      const waitingFrom = facts.productionDate ? null : orderedOn;
      readyToBook.push({
        projectId: project.id,
        workspace: project.project_name || 'Untitled',
        customer: project.customer_name || '—',
        productionDate: facts.productionDate,
        orderedOn,
        daysWaiting: waitingFrom ? Math.max(0, calendarDaysBetween(waitingFrom, today)) : 0,
        kind: facts.supplyOnly ? 'delivery' : 'install',
        holdUps,
        href: workspaceHref(project.id, facts.productionDate ? 'implementation' : 'supplier'),
      });
    }
    installSpans.forEach(({ cardId, span }) => {
      span.days.forEach((dayKey) => {
        if (!weekKeys.has(dayKey)) return;
        week.push({
          projectId: project.id,
          jobCardId: cardId,
          dayKey,
          time: toInstallTimeOnly(span.start) || '—',
          spanNote: installSpanNote(span.days, dayKey),
          workspace: project.project_name || 'Untitled',
          customer: project.customer_name || '—',
          crew: crewLabel(cardId ? namesByCard.get(cardId) || [] : allNames),
          source: span.overridden ? 'Override' : 'Calendar',
          completion: cardId ? jobCardCompletion(completionsByCard.get(cardId) || []) : null,
          href: workspaceHref(project.id, 'implementation'),
        });
      });
    });
  });

  attention.sort((a, b) => (
    Number(b.exception) - Number(a.exception)
    || b.ageDays - a.ageDays
    || a.workspace.localeCompare(b.workspace)
  ));
  week.sort((a, b) => a.dayKey.localeCompare(b.dayKey) || a.time.localeCompare(b.time));
  scheduled.sort((a, b) => (
    compareCurrentToOldest(a.dayKey, b.dayKey, today)
    || a.workspace.localeCompare(b.workspace)
  ));
  unscheduled.sort((a, b) => (
    compareCurrentToOldest(a.productionDate, b.productionDate, today)
    || a.workspace.localeCompare(b.workspace)
  ));

  const toReasonRows = (counts: Record<string, number>) =>
    Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));

  return {
    activeCount: active.length,
    queueCounts,
    stateCounts,
    exceptionCount: attention.filter((row) => row.exception).length,
    attention,
    board,
    week,
    scheduled,
    unscheduled,
    money: [money.deposit, money.works, money.variation, money.accepted].filter((row) => row.count > 0),
    officeReasons: toReasonRows(officeCount),
    customerReasons: toReasonRows(customerCount),
    needsOffice: attention.filter((row) => row.side === 'office').length,
    waitingCustomer: attention.filter((row) => row.side === 'customer').length,
    installsThisWeek: new Set(week.map((row) => `${row.projectId}:${row.jobCardId || ''}`)).size,
    issuedUnpaidTotal,
    pendingClosure: pendingClosure.sort((a, b) => a.fittedOn.localeCompare(b.fittedOn) || a.workspace.localeCompare(b.workspace)),
    installsAtRisk: installsAtRisk.sort((a, b) => a.nextDay.localeCompare(b.nextDay) || a.workspace.localeCompare(b.workspace)),
    readyToBook: readyToBook.sort((a, b) => {
      if (a.productionDate && b.productionDate) return a.productionDate.localeCompare(b.productionDate) || a.workspace.localeCompare(b.workspace);
      if (a.productionDate || b.productionDate) return a.productionDate ? -1 : 1;
      return b.daysWaiting - a.daysWaiting || a.workspace.localeCompare(b.workspace);
    }),
    snapshots,
  };
}

/** Needs attention rows a gate card already lists for the same workspace, so they are not shown twice. */
const GATE_ATTENTION_KINDS: Array<{ kinds: AttentionKind[]; rows: (ops: WorkspaceOperations) => Array<{ projectId: string }> }> = [
  { kinds: ['install_order_missing', 'install_deposit_missing', 'install_unassigned'], rows: (ops) => ops.installsAtRisk },
  { kinds: ['ordered_no_production', 'production_no_install'], rows: (ops) => ops.readyToBook },
];

export function attentionOutsideGates(ops: WorkspaceOperations): AttentionRow[] {
  const covered = new Set(GATE_ATTENTION_KINDS.flatMap(({ kinds, rows }) => (
    rows(ops).flatMap((row) => kinds.map((kind) => `${row.projectId}:${kind}`))
  )));
  return ops.attention.filter((row) => !covered.has(`${row.projectId}:${row.kind}`));
}

export function emptyWorkspaceOperations(): WorkspaceOperations {
  return assembleWorkspaceOperations({
    projects: [],
    documents: [],
    cards: [],
    assignees: [],
    events: [],
  });
}
